/**
 * SwimLaneOverlay.tsx
 *
 * Renderiza guias horizontais leves (raias) sobre o canvas.
 * Cada raia tem:
 *  - Altura de 220px + 60px de gap entre raias
 *  - Background ultra-sutil: rgba(255,255,255,0.015)
 *  - Separador: 1px dashed rgba(255,255,255,0.15)
 *  - Label no topo à esquerda: 10px, font-weight 600, opacity 0.50
 */

import React from 'react';
import type { SwimLaneInfo } from '../utils/autoLayoutEngine';

interface SwimLaneOverlayProps {
  /** Definições das raias (posição, label, altura) */
  lanes: SwimLaneInfo[];
  /** Se true, renderiza o overlay; se false, oculta */
  visible: boolean;
  /** Offset X do viewport (pan.x) para manter label sticky */
  panX?: number;
  /** Escala atual do canvas para compensar no label sticky */
  scale?: number;
}

const SwimLaneOverlay: React.FC<SwimLaneOverlayProps> = React.memo(({
  lanes,
  visible,
  panX = 0,
  scale = 1,
}) => {
  if (!visible || lanes.length === 0) return null;

  // Debug log
  console.log('[SwimLaneOverlay] Rendering', { 
    laneCount: lanes.length, 
    visible, 
    lanes: lanes.map(l => ({ label: l.label, y: l.y, height: l.height }))
  });

  // Calcula a largura total necessária para cobrir o canvas
  const totalWidth = 10000;
  // Posição X compensada para sticky label
  const labelX = Math.max(0, (-panX / scale) + 8);

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: 0,
        top: 0,
        width: totalWidth,
        zIndex: 2,
      }}
    >
      {lanes.map((lane, index) => (
        <div
          key={`swimlane-${index}-${lane.label}`}
          className="absolute"
          style={{
            left: 0,
            top: lane.y,
            width: totalWidth,
            height: lane.height,
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            borderBottom: '2px solid rgba(59, 130, 246, 0.4)',
            borderTop: index === 0 ? '2px solid rgba(59, 130, 246, 0.4)' : undefined,
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.1)',
          }}
        >
          {/* Label sticky à esquerda, centralizado verticalmente */}
          <div
            className="absolute select-none"
            style={{
              left: labelX,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 14,
              fontWeight: 800,
              opacity: 1,
              color: '#1e3a5f',
              whiteSpace: 'nowrap',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textShadow: '0 1px 0 rgba(255,255,255,0.8)',
              padding: '4px 12px',
              background: 'rgba(59, 130, 246, 0.15)',
              border: '1px solid rgba(59, 130, 246, 0.35)',
              borderRadius: '4px',
            }}
          >
            {lane.label}
          </div>
        </div>
      ))}
    </div>
  );
});

SwimLaneOverlay.displayName = 'SwimLaneOverlay';

export default SwimLaneOverlay;
